import React, { useState } from 'react'
import ListPagination from 'Components/ListPagination/ListPagination'
import type { ListPageSize } from 'Hooks/useListPagination'
import { userBXService } from 'BoostXpressAdmin/Services/userBXService'
import { handleError, handleSuccess } from 'Helpers'
import Swal from 'sweetalert2'
import { ToastContainer } from 'react-toastify'
import filterIcon from 'BoostXpressAdmin/Assets/Icon/Users/filter.svg'

type AttendantStatus = 'Active' | 'Inactive'

export type AttendantsTableRow = {
  id: string
  name: string
  email: string
  phone: string
  station: string
  role?: string
  referralCode: string
  createdAt: string
  lastLoginAt: string | null
  status: AttendantStatus
}

const getStatusLabelClass = (status: AttendantStatus) => {
  if (status === 'Active') return 'bx-status-pill bx-status-pill--active'
  return 'bx-status-pill bx-status-pill--inactive'
}

type Props = {
  title?: string
  primaryActionLabel?: string
  rows: AttendantsTableRow[]
  isLoading?: boolean
  onPrimaryAction?: () => void
  onViewAction?: (id: string) => void
  searchTerm?: string
  onSearchTermChange?: (value: string) => void
  page: number
  setPage: (value: number) => void
  onPageSizeChange: (value: ListPageSize) => void
  total: number
  limit: number
  reloadList: () => void
  disable: string
  setDisable: (value: string) => void
}

const AttendantsCommissionTable = ({
  title = 'All Users',
  primaryActionLabel = '+ Add User',
  rows = [],
  isLoading,
  onPrimaryAction,
  onViewAction,
  searchTerm = '',
  onSearchTermChange,
  page,
  setPage,
  onPageSizeChange,
  total,
  limit,
  reloadList,
  disable,
  setDisable,
}: Props) => {
  const [showFilters, setShowFilters] = useState(false)

  const [isLoadingDisable, setIsLoadingDisable] = useState(false)

  const handleDisableUser = async (userId: string) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to disable this attendant, this action is not reversible. Are you sure you want to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#186AFF',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, disable!',
    }).then(async (result) => {
      if (!result.isConfirmed) return
      setIsLoadingDisable(true)

      await userBXService.disable(userId).then(
        async () => {
          // Refresh user data
          setIsLoadingDisable(false)
          reloadList()
          handleSuccess('User disabled successfully')
        },
        (e) => {
          handleError(e)

          setIsLoadingDisable(false)
        },
      )
    })
  }

  const handleActivateUser = async (userId: string) => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'You are about to activate this attendant, this action is not reversible. Are you sure you want to continue?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#186AFF',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, activate!',
    }).then(async (result) => {
      if (!result.isConfirmed) return
      setIsLoadingDisable(true)

      await userBXService.enable(userId).then(
        async () => {
          // Refresh user data
          setIsLoadingDisable(false)
          reloadList()
          handleSuccess('User activated successfully')
        },
        (e) => {
          handleError(e)

          setIsLoadingDisable(false)
        },
      )
    })
  }

  return (
    <section className='bx-attendance-table'>
      <div className='bx-attendance-table__header'>
        <h3 className='bx-attendance-table__title'>{title}</h3>
      </div>

      <div className='bx-attendance-table__toolbar row  align-items-center'>
        <div className='col-12 col-lg'>
          <div className='bx-attendance-search w-100'>
            <div className='bx-attendance-search__icon'>
              <svg
                width='17'
                height='17'
                viewBox='0 0 24 24'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M21 21L16.5 16.5M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z'
                  stroke='#868484'
                  strokeWidth='2'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
            </div>

            <input
              type='text'
              className='bx-attendance-search__input'
              placeholder='Search Attendants...'
              value={searchTerm}
              onChange={(e) => onSearchTermChange?.(e.target.value)}
            />
          </div>
        </div>

        {/* <div className='col-12 col-lg-auto'>disabled</div> */}
        <div className='col-12 col-lg-auto position-relative'>
          <button className='bx-action-btn w-100' onClick={() => setShowFilters(!showFilters)}>
            <img src={filterIcon} alt='' className='bx-action-icon' />
            <span className='bx-action-label'>Filters</span>
          </button>

          {showFilters && (
            <div className='bx-users-filters-dropdown'>
              <div className='bx-filters-header'>
                <h5>Filter Attendants</h5>
                <button className='bx-filters-close' onClick={() => setShowFilters(false)}>
                  ×
                </button>
              </div>

              <div className='bx-filters-body'>
                <div className='bx-filter-group'>
                  <label>Account Status</label>
                  <select
                    value={disable}
                    onChange={(e) => setDisable(e.target.value)}
                    className='form-control'
                  >
                    <option value=''>All Users</option>
                    <option value='true'>Disabled</option>
                    <option value='false'>Active</option>
                  </select>
                </div>
              </div>

              <div className='bx-filters-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => {
                    setDisable('')
                  }}
                >
                  Clear Filters
                </button>
                <button className='btn btn-primary' onClick={() => setShowFilters(false)}>
                  Apply Filters
                </button>
              </div>
            </div>
          )}
        </div>

        <div className='col-12 col-lg-auto'>
          <button
            type='button'
            className='bx-attendance-table__primary-btn'
            onClick={onPrimaryAction}
            disabled={!onPrimaryAction}
          >
            {primaryActionLabel}
          </button>
          {/* <div className='d-flex gap-2 justify-content-lg-end'>
            <div className='bx-attendance-filter'>
              <img src={filterIcon} alt='' className='bx-attendance-filter__icon' />

              <select className='form-select bx-attendance-filter__select' disabled>
                <option>Server Filter</option>
              </select>
            </div>
          </div> */}
        </div>
      </div>

      <div className='bx-attendance-table__card'>
        <div className='table-responsive bx-attendance-table__responsive'>
          <table className='table bx-attendance-table__table align-middle mb-0'>
            <thead>
              <tr>
                <th>#</th>
                <th>Attendant</th>
                <th>Role</th>
                <th>Station</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Referral Code</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9} className='text-center py-4'>
                    Loading...
                  </td>
                </tr>
              ) : rows.length ? (
                rows.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{(page - 1) * limit + idx + 1}</td>

                    <td className='bx-attendance-table__name'>{r.name}</td>

                    <td>{r.role || 'Attendant'}</td>

                    <td>{r.station}</td>

                    <td className='bx-attendance-table__truncate'>{r.email}</td>

                    <td>{r.phone || '—'}</td>

                    <td>{r.referralCode || '—'}</td>

                    <td>
                      <span className={getStatusLabelClass(r.status)}>{r.status}</span>
                    </td>

                    <td>
                      <div className='bx-attendance-table__actions'>
                        <button
                          type='button'
                          className='bx-attendance-table__action-btn'
                          onClick={() => onViewAction?.(r.id)}
                          disabled={!onViewAction || r.role === 'Manager' || r.role === 'Associate'}
                        >
                          View
                        </button>

                        {r.status !== 'Inactive' && (
                          <button
                            type='button'
                            className='bx-attendance-table__action-btn bx-attendance-table__action-btn--danger'
                            disabled={
                              isLoadingDisable || r.role === 'Manager' || r.role === 'Associate'
                            }
                            onClick={() => handleDisableUser(r.id)}
                          >
                            Suspend
                          </button>
                        )}
                        {r.status === 'Inactive' && (
                          <button
                            type='button'
                            className='bx-attendance-table__action-btn bx-attendance-table__action-btn--danger text-success'
                            disabled={
                              isLoadingDisable || r.role === 'Manager' || r.role === 'Associate'
                            }
                            onClick={() => handleActivateUser(r.id)}
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8} className='text-center py-4'>
                    No results found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          page={page}
          limit={limit}
          total={total}
          disabled={isLoading}
          onPageChange={setPage}
          onPageSizeChange={onPageSizeChange}
        />
      </div>
      <ToastContainer />
    </section>
  )
}

export default AttendantsCommissionTable
